import React, { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import Breadcrumb from 'components/breadcrumb'
import styles from './lesson-overview-page.module.css'
import { LessonOverviewProvider, useLessonOverview } from './lesson-overview-context'
import { LessonOverviewTable } from './lesson-overview-table'
import { LessonProvider } from 'contexts/lesson-context'
import { DifficultyPills } from 'components/tables/difficulty-pills'

const DIFFICULTY_ORDER = ['Emerging', 'Intermediate', 'Advanced']

const Content = ({ difficulty, onChangeDifficulty }: { difficulty: string; onChangeDifficulty: (d: string) => void }) => {
  const { classId } = useParams()
  const { data, isLoading, allDifficultiesWithData } = useLessonOverview()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [hasAutoSelected, setHasAutoSelected] = useState(false)

  const handleDifficultyChange = (d: string) => {
    onChangeDifficulty(d)
    navigate(`?difficulty=${encodeURIComponent(d)}`, { replace: true })
  }

  useEffect(() => {
    if (hasAutoSelected || allDifficultiesWithData.length === 0) return
    const fromUrl = searchParams.get('difficulty') || ''
    const target = (fromUrl && allDifficultiesWithData.includes(fromUrl))
      ? fromUrl
      : DIFFICULTY_ORDER.find(d => allDifficultiesWithData.includes(d)) || allDifficultiesWithData[0]
    onChangeDifficulty(target)
    navigate(`?difficulty=${encodeURIComponent(target)}`, { replace: true })
    setHasAutoSelected(true)
  }, [allDifficultiesWithData, hasAutoSelected])

  return (
    <div className={styles.container}>
      <Breadcrumb items={[
        { label: 'My Lesson Library', path: '/lessons' },
        { label: 'Classroom', path: `/lessons?classId=${classId}` },
        { label: data ? `${data.lessonName}: Grades` : 'Lesson Overview' }
      ]} />

      <div className={styles.row}>
        <DifficultyPills
          currentDifficulty={difficulty}
          handleDifficultyChange={handleDifficultyChange}
          includeAll={false}
          availableDifficulties={allDifficultiesWithData.length > 0 ? allDifficultiesWithData : undefined}
        />
      </div>

      {isLoading && <div>Loading...</div>}
      {!isLoading && data && (
        <LessonOverviewTable questions={data.questions} difficulty={difficulty} />
      )}
    </div>
  )
}

export const LessonOverviewPage = () => {
  const { classId, lessonId } = useParams()
  const [difficulty, setDifficulty] = React.useState<string>('Emerging')
  if (!classId || !lessonId) return null
  return (
    <LessonProvider lessonId={lessonId}>
      <LessonOverviewProvider classId={classId} lessonId={lessonId} difficulty={difficulty}>
        <Content difficulty={difficulty} onChangeDifficulty={setDifficulty} />
      </LessonOverviewProvider>
    </LessonProvider>
  )
}

export default LessonOverviewPage
